<template>
  <div class="flex flex-row w-full h-14" @dragover="dragOver" @dragenter.prevent :dropzone="rearrangeable ? 'move' : ''">
    <Button
      v-if="showAdd"
      v-tooltip.right="`Add New ${tabName}`"
      icon="pi pi-plus"
      outlined
      class="h-10! w-10! mt-auto"
      @click="tabAction = 'Add'"
    ></Button>
    <Tabs ref="tabMenu" v-model:value="activeTab" class="mt-2 overflow-x-auto h-fit w-full rounded-t-lg dynamic" :class="showAdd ? '' : 'mx-1'">
      <TabList ref="tabList" pt:tabList:class="bg-transparent">
        <Tab
          v-for="(tab, index) in tabs"
          :value="index"
          :class="activeTab === index ? 'border-b-8 mt-0' : 'border-surface-300 dark:border-surface-600 mt-2'"
          class="py-2 border-t-1! shadow rounded-t-lg border-x-1! transition-[margin]"
          :draggable="rearrangeable"
          @dragstart="(e: DragEvent) => dragStart(e, index)"
          @drag="drag"
          @dragend="dragEnd"
        >
          <Button
            v-if="showEdit"
            severity="success"
            text
            rounded
            icon="pi pi-pencil"
            class="h-6! w-6! p-0! -ml-2 text-sm"
            @click="
              tabEditing = index;
              tabAction = 'Edit';
            "
          ></Button>
          <span class="font-bold p-2">{{ tab.label }}</span>
          <Button
            v-if="showDelete"
            severity="danger"
            text
            rounded
            icon="pi pi-times"
            class="h-6! w-6! p-0! -mr-2 text-sm"
            @click="tryDeleteTab(index)"
          ></Button>
        </Tab>
      </TabList>
    </Tabs>
  </div>
  <TSDialog :label="`${tabAction} ${tabName}`" v-model:visible="tabAction" :icon="tabAction === 'Add' ? 'plus' : 'pencil'" size="w-[600px]!">
    <template #content>
      <div class="flex flex-col gap-2 py-5">
        <label for="tabName" class="font-bold">{{ tabName }} Name:</label>
        <InputText name="tabName" type="text" class="h-10" v-model="newTabName" autofocus v-on:keyup.enter="submitTabAction" />
        <SubmitButton :label="`${tabAction} ${tabName}`" icon="plus" @click="submitTabAction"></SubmitButton>
      </div>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import type { MenuItem } from "primevue/menuitem";
import { useConfirm } from "primevue/useconfirm";

const activeTab = defineModel<number>("activeTab", { required: true });
const tabs = defineModel<MenuItem[]>("tabs", { required: true });

const tabMenu = ref();
const tabList = ref();

const tabEditing = ref(0);

const tabAction = ref<"Add" | "Edit" | null>(null);
const newTabName = ref("");

const confirm = useConfirm();

const emit = defineEmits<{
  addTab: [tabName: string];
  editTab: [tabName: string, tabEditedIndex: number];
  deleteTab: [index: number];
  tabChanged: [];
  tabsRearranged: [];
}>();

watch(
  () => activeTab.value,
  (n: number) => emit("tabChanged")
);

interface Props {
  showAdd?: boolean;
  showDelete?: boolean;
  showEdit?: boolean;
  allowDuplicates?: boolean;
  tabName?: string;
  rearrangeable?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  showAdd: false,
  showDelete: false,
  showEdit: false,
  allowDuplicates: false,
  tabName: "Tab",
  rearrangeable: false,
});

const submitTabAction = (): void => {
  if (newTabName.value == null || newTabName.value.trim() === "") {
    error("Invalid Name", `The ${props.tabName.toLowerCase()} name may not be empty`);
    return;
  }

  if (!props.allowDuplicates) {
    const existingTab = tabs.value.find((tab: MenuItem) => tab.label?.toString().toUpperCase() === newTabName.value.toUpperCase());
    let valid = true;
    if (existingTab != null) valid = false;
    if (tabAction.value === "Edit" && existingTab == tabs.value[activeTab.value]) valid = true;
    if (!valid) {
      error("Invalid Name", `There is already a ${props.tabName.toLowerCase()} with this name`);
      return;
    }
  }

  if (tabAction.value === "Add") {
    activeTab.value = tabs.value.length;
    emit("addTab", newTabName.value);
  } else {
    emit("editTab", newTabName.value, tabEditing.value);
  }

  newTabName.value = "";
  tabAction.value = null;
};

const tryDeleteTab = (index: number): void => {
  confirmPopup(
    confirm,
    `Delete ${props.tabName}`,
    `Are you sure you want to delete the ${tabs.value[index].label} ${props.tabName.toLowerCase()}?`,
    () => emit("deleteTab", index),
    () => info("Delete Cancelled", "")
  );
};

const currentDragTab = ref<HTMLElement>();
let currentDragTabIndex: number = 0;
const dragStart = (e: DragEvent, index: number): void => {
  activeTab.value = index;
  if (e.target instanceof HTMLElement) {
    const data = e.dataTransfer;
    if (data) {
      data.setDragImage(new Image(), 0, 0);
      data.effectAllowed = "linkMove";
    }
    currentDragTab.value = e.target;
    currentDragTab.value.classList.add("is-dragging");
    currentDragTabIndex = index;
  }
};

const dragOver = (e: DragEvent): void => {
  e.preventDefault();

  if (tabs.value.length <= 1) {
    return;
  }

  const closestTab = getClosestTabInfo(e.clientX, true);
  moveTab(closestTab);
};

const drag = (e: DragEvent): void => {
  e.preventDefault();

  if (tabs.value.length <= 1) {
    return;
  }

  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.dropEffect = "move";
  }
};

const dragEnd = (e: DragEvent): void => {
  e.preventDefault();
  currentDragTab.value?.classList.remove("is-dragging");
  currentDragTab.value = undefined;

  let closestTab = getClosestTabInfo(e.clientX, false);
  moveTab(closestTab);
};

const getClosestTabInfo = (currentX: number, updateIndicators: boolean): number => {
  if (tabs.value.length <= 1) return 0;

  let tabListItems: HTMLLIElement[] = Array.from(tabList.value.$refs.tabs.children);
  tabListItems = tabListItems.filter((item) => item instanceof HTMLButtonElement); //Remove the active status bar
  tabListItems.sort((a, b) => a.getBoundingClientRect().x - b.getBoundingClientRect().x);

  let min = Number.MAX_VALUE;
  let closestTabIndex = 0;

  for (let i = 0; i <= tabListItems.length; i++) {
    let listItem = tabListItems[i === tabListItems.length ? tabListItems.length - 1 : i];
    let rect: DOMRect = listItem.getBoundingClientRect();
    let distance: number = 0;

    if (i === tabListItems.length) {
      distance = Math.abs(currentX - rect.right);
    } else {
      distance = Math.abs(currentX - rect.left);
    }

    if (distance < min) {
      min = distance;
      closestTabIndex = i;
    }
  }

  return closestTabIndex;
};

const moveTab = (closestTab: number): void => {
  if (closestTab === currentDragTabIndex || closestTab - 1 === currentDragTabIndex) {
    return;
  }

  const tempTab = tabs.value[currentDragTabIndex];
  tabs.value.splice(currentDragTabIndex, 1);
  const insertIndex = closestTab > currentDragTabIndex ? closestTab - 1 : closestTab;
  tabs.value.splice(insertIndex, 0, tempTab);
  currentDragTabIndex = insertIndex;
  activeTab.value = insertIndex;

  for (let i = 0; i < tabs.value.length; i++) {
    tabs.value[i].key = i.toString();
  }

  emit("tabsRearranged");
};
</script>
